import { Field, Form, Formik, useFormikContext } from "formik";
import Button from "pages/_components/Button";
import GridTable from "pages/_components/GridTable/GridTable";
import Notification from "pages/_components/Notification";
import { bool } from "prop-types";
import React, { useRef } from "react";
import { Col, Row } from "react-bootstrap";
import MainContainer from "pages/_components/MainContainer";
import ResponsiveHeader from "pages/_components/header";
import CustomText from "pages/_components/CustomText/CustomText";
import * as configUtils from "util/config";
import { push } from "connected-react-router";
import { useDispatch, useSelector } from "react-redux";
import { actions as comexActions } from "reducers/comex";
import { isNaN } from "lodash";
import ComexStepper from "../_components/ComexStepper";
import DocumentUploader from "../_components/DocumentUploader";
import useDDJJEnabledOptions from "./hooks/useDDJJEnabledOptions";
import DDJJItem from "../_components/DDJJItem";
import DDJJSubOptions from "../_components/DDJJSubOptions";
import DDJJSubOptionsV17 from "./_components/DDJJSubOptionsV17";
import DDJJSubOptionsV14 from "./_components/DDJJSubOptionsV14";
import DDJJSubOptionsV19 from "./_components/DDJJSubOptionsV19";

const FORM_ID = "comex.ddjj.general";
const StepDDJJGeneral = ({ isDesktop }) => {
    const dispatch = useDispatch();

    const currentRow = useRef(2);
    const { enabledOptions } = useDDJJEnabledOptions();
    const ddjjKeys = Array.from({ length: 21 }, (_, i) => `v${i + 1}`);
    const { comexTransfer = {}, comexId = null } = useSelector((state) => state.comex);
    const { ddjj = {}, concept = "" } = comexTransfer;
    const codeDDJJGeneral = configUtils.get("comex.thuban.code.ddjjgeneral", 1);

    const renderSubOptionsWithRows = (vKey, startingRow) => {
        const configPath = `${FORM_ID}.${vKey}.options`;
        const subOptions = configUtils.getArray(configPath);

        if (!subOptions?.length) {
            return { jsx: null, rowsUsed: 0 };
        }

        let Component = DDJJSubOptions;

        if (vKey === "v14") {
            Component = DDJJSubOptionsV14;
        } else if (vKey === "v17") {
            Component = DDJJSubOptionsV17;
        } else if (vKey === "v19") {
            Component = DDJJSubOptionsV19;
        }

        return {
            jsx: <Component formId={FORM_ID} option={vKey} rowStart={startingRow} subOptions={subOptions} />,
            rowsUsed: subOptions.length,
        };
    };

    const extendedStructure = {
        v14: "all",
        v19: "all",
    };

    const generateInitialValues = () => {
        const initValues = {};

        ddjjKeys.forEach((vKey) => {
            const subOptions = configUtils.getArray(`${FORM_ID}.${vKey}.options`);
            if (!subOptions?.length) {
                return;
            }

            const extendedSubOptions = extendedStructure[vKey] || [];
            const isAllExtended = extendedSubOptions === "all";

            initValues[vKey] = {};

            subOptions.forEach((opt) => {
                const isExtended = isAllExtended || extendedSubOptions.includes(opt);
                const existingValues = ddjj[vKey]?.[opt];

                // Caso especial: v17.option1 con valores anidados
                if (vKey === "v17" && opt === "option1") {
                    initValues[vKey][opt] = ddjj[vKey]?.[opt] ?? false;
                    initValues[vKey][`${opt}Values`] = {
                        anual: ddjj[vKey]?.[`${opt}Values`]?.anual ?? false,
                        trimestral: ddjj[vKey]?.[`${opt}Values`]?.trimestral ?? false,
                        periodEnd: ddjj[vKey]?.[`${opt}Values`]?.periodEnd ?? "",
                    };
                }
                // Opciones extendidas normales
                else if (isExtended) {
                    initValues[vKey][opt] = {
                        checked: existingValues?.checked ?? false,
                        value: existingValues?.value ?? "",
                    };
                }
                // Opciones simples
                else {
                    initValues[vKey][opt] = existingValues ?? false;
                }
            });
        });

        // Documento si v10 está habilitado
        if (enabledOptions.v10) {
            initValues.v10Document = ddjj.v10Document ?? "";
            initValues.v10DocumentName = ddjj.v10DocumentName ?? "";
        }

        return initValues;
    };

    const getIsAllAccepted = (values) => {
        const isMainValid = ddjjKeys.every((vKey) => {
            if (!enabledOptions[vKey]) {
                return true;
            }

            const subOptions = configUtils.getArray(`${FORM_ID}.${vKey}.options`);
            if (!subOptions?.length) {
                return true;
            }

            const extended = extendedStructure[vKey] || [];
            const isAllExtended = extended === "all";

            const group = values?.[vKey];
            if (!group) {
                return false;
            }

            const atLeastOneChecked = subOptions.some((subOpt) => {
                const isExtended = isAllExtended || extended.includes(subOpt);
                const val = group[subOpt];

                if (isExtended) {
                    const isChecked = val?.checked === true;
                    if (!isChecked) {
                        return false;
                    }
                    if (vKey === "v19" && subOpt === "option1") {
                        return true;
                    }
                    return typeof val.value === "string" ? val.value.trim() : String(val.value ?? "");
                }
                return val === true;
            });

            return atLeastOneChecked;
        });

        if (!isMainValid) {
            return false;
        }

        const subOptionsV17 = configUtils.getArray(`${FORM_ID}.v17.options`);
        if (enabledOptions.v17 && subOptionsV17.includes("option1")) {
            const option1 = values?.v17?.option1;
            if (option1) {
                const { anual, trimestral, periodEnd } = values?.v17?.option1Values || {};

                if (trimestral) {
                    const isValidDate = periodEnd instanceof Date && !isNaN(periodEnd);
                    return isValidDate;
                }

                if (!anual && !trimestral) {
                    return false;
                }
            }
        }

        const showUploader = values?.v10?.option2 === true;
        if (showUploader) {
            if (!values?.v10Document || !values?.v10DocumentName) {
                return false;
            }
        }

        return true;
    };

    const saveComexRequest = (isContinue, values) => {
        const _comexTransferRequest = {
            ...comexTransfer,
            ddjj: {
                ...values,
            },
            step: "stepDDJJ",
            comexId,
            isContinue,
        };
        dispatch(comexActions.createComexTransfer(_comexTransferRequest));
    };

    const handleSubmit = (values) => {
        dispatch(
            comexActions.setComexTransfer({
                ...comexTransfer,
                ddjj: {
                    ...values,
                },
            }),
        );
        saveComexRequest(true, values);
        dispatch(push("/comex/confirm"));
    };

    const getBackLink = () => {
        const isService = concept.includes("S");
        const isGoods = concept.includes("B");
        if (isService) {
            return "/comex/ddjj/service";
        }
        if (isGoods) {
            return "/comex/ddjj/goods";
        }
        return "/comex/documentation";
    };

    return (
        <>
            {isDesktop && <ComexStepper isDesktop={isDesktop} currentStep={8} />}
            <Notification scopeToShow="ComexNotification" />
            <ResponsiveHeader ticket metaTitle="transfers.comex.title" backLinkTo={getBackLink()} />

            <MainContainer showLoader={false}>
                <Notification scopeToShow="UploadNotification" />
                <Formik initialValues={generateInitialValues()} onSubmit={(values) => handleSubmit(values)}>
                    {({ values, isValid }) => {
                        const showUploader = values?.v10?.option2 === true;
                        const canSubmit = showUploader ? !!values.v10Document && !!values.v10DocumentName : true;

                        return (
                            <Form>
                                <GridTable className="form">
                                    <GridTable.Container columns={12} columnsTemplate="fixed">
                                        <GridTable.Data
                                            columnStart={2}
                                            flexDirection="column"
                                            columnWidth={10}
                                            rowStart={1}>
                                            <CustomText
                                                text="comex.ddjj.general.title"
                                                textType="I18N"
                                                className="comex-title"
                                            />
                                        </GridTable.Data>
                                        {ddjjKeys.map((vKey) => {
                                            if (!enabledOptions[vKey]) {
                                                return null;
                                            }

                                            const itemRow = currentRow.current++;
                                            const { jsx: subOptionsJSX, rowsUsed } = renderSubOptionsWithRows(
                                                vKey,
                                                currentRow.current,
                                            );
                                            currentRow.current += rowsUsed;

                                            return (
                                                <React.Fragment key={vKey}>
                                                    <DDJJItem formId={FORM_ID} vKey={vKey} row={itemRow} />
                                                    {subOptionsJSX}
                                                    {vKey === "v10" && (
                                                        <>
                                                            {/* DocumentUploader */}
                                                            {showUploader && (
                                                                <GridTable.Data
                                                                    columnStart={2}
                                                                    columnWidth={10}
                                                                    flexDirection="column"
                                                                    rowStart={currentRow.current++}
                                                                    key={`${vKey}-documentUploader`}>
                                                                    <CustomText
                                                                        className="document-title"
                                                                        text={`${FORM_ID}.${vKey}.fileUpload`}
                                                                        textType="I18N"
                                                                    />
                                                                    <Field
                                                                        component={DocumentUploader}
                                                                        idForm={FORM_ID}
                                                                        name="v10Document"
                                                                        label="file.upload.input.label"
                                                                        extensionDocument="pdf"
                                                                        documentCode={codeDDJJGeneral}
                                                                        defaultDocument={{
                                                                            documentId: ddjj?.v10Document,
                                                                            documentName: ddjj?.v10DocumentName,
                                                                        }}
                                                                        withDocumentName
                                                                        overrideDocumentName="SolicitudVentaCambioPersonasControlantes"
                                                                    />
                                                                </GridTable.Data>
                                                            )}
                                                            {/* Cláusula adicional v10.detail */}
                                                            <DDJJItem
                                                                formId={FORM_ID}
                                                                vKey="v10.detail"
                                                                row={currentRow.current++}
                                                            />
                                                        </>
                                                    )}
                                                    {vKey === "v11" ||
                                                        (vKey === "v19" && (
                                                            <>
                                                                {/* Cláusula adicional vkey.detail */}
                                                                <DDJJItem
                                                                    formId={FORM_ID}
                                                                    vKey={`${vKey}.detail`}
                                                                    row={currentRow.current++}
                                                                />
                                                            </>
                                                        ))}
                                                </React.Fragment>
                                            );
                                        })}
                                    </GridTable.Container>
                                </GridTable>
                                <Row className="form-actions">
                                    <Col xs={12} md={4} mdOffset={4}>
                                        <Button type="submit" className="btn-primary" label="global.continue" />
                                    </Col>
                                </Row>
                            </Form>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};

StepDDJJGeneral.propTypes = {
    isDesktop: bool.isRequired,
};

export default StepDDJJGeneral;
